import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import Button from '@mui/material/Button';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { clientHubContact } from '@/components/client-hub/api';
import style from '@/components/referral/style.module.scss';
import {
  EMAIL_FORM_ERR_FR,
  ENTER_CHARS_FORM_ERR,
  ENTER_VALID_FORM_ERR,
  FORM_ERRORS,
  NAME_FORM_ERR,
  NAME_FORM_ERR_FR,
} from '@/utils/constants';

type Local = 'en' | 'fr';

type Inputs = {
  fullName: string;
  phone: string;
  email: string;
  message: string;
};

interface ClientHubContactWebformProps {
  track: any;
  facilityId: string;
  local: Local;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  marginBottom: '10px',
  boxSizing: 'border-box',
  background: 'rgba(224, 225, 227, 0.14)',
  border: '1px solid rgba(2, 35, 94, 0.2)',
  borderRadius: '4px',
};

const translations: Record<Local, any> = {
  en: {
    labels: {
      fullName: 'Full name*',
      phone: 'Phone number on file*',
      email: 'Email',
      message: 'Message*',
      submitButton: 'Submit',
    },
    successModal: {
      header: 'Thank you for reaching out!',
      body: (firstName: string) =>
        `We have received your message ${firstName}, and one of our team members will give you a call shortly.`,
      ticketLabel: 'Client care ticket id:',
      queueText:
        'We’ve got you in queue and will reach out directly.\nTalk soon, and thank you for your patience.',
      closeButton: 'Close',
    },
    errors: {
      nameRequired: NAME_FORM_ERR,
      namePattern: ENTER_CHARS_FORM_ERR,
      phoneRequired: FORM_ERRORS.EN.ENTER_PHONE,
      phonePattern: FORM_ERRORS.EN.ENTER_PHONE,
      emailPattern: ENTER_VALID_FORM_ERR,
      messageRequired: 'Message is required',
    },
  },
  fr: {
    labels: {
      fullName: 'Nom au complet*',
      phone: 'Téléphone associé*',
      email: 'Courriel',
      message: 'Message*',
      submitButton: 'Soumettre',
    },
    successModal: {
      header: 'Merci pour votre message !',
      body: (firstName: string) =>
        `Nous avons reçu votre message, ${firstName}, et un membre de notre équipe vous appellera sous peu.`,
      ticketLabel: 'ID de ticket client :',
      queueText:
        'Votre demande est en file d’attente et nous vous contacterons directement.\nÀ bientôt, et merci de votre patience.',
      closeButton: 'Fermer',
    },
    errors: {
      nameRequired: NAME_FORM_ERR_FR,
      namePattern: NAME_FORM_ERR_FR,
      phoneRequired: FORM_ERRORS.FR.PHONE,
      phonePattern: FORM_ERRORS.FR.PHONE,
      emailPattern: EMAIL_FORM_ERR_FR,
      messageRequired: 'Le message est requis',
    },
  },
};

const ClientHubContactWebform: React.FC<ClientHubContactWebformProps> = ({
  track,
  facilityId,
  local,
}) => {
  const t = translations[local];

  const [caseId, setCaseId] = useState('');
  const [fullName, setFullName] = useState('');

  const {
    register,
    getValues,
    reset,
    trigger,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      message: '',
    },
  });

  let formSubmissionLock = false;

  const clickSubmit = async () => {
    if (formSubmissionLock) return;
    formSubmissionLock = true;
    track('Submit contact web form');

    const formIsValid = await trigger();
    if (!formIsValid) {
      // eslint-disable-next-line no-console
      console.log('Errors found', errors);
      formSubmissionLock = false;
      return;
    }

    const body = {
      lcode: facilityId,
      fullName: getValues('fullName'),
      phone: getValues('phone'),
      email: getValues('email'),
      message: getValues('message'),
    };

    const response = await clientHubContact(body);
    if (response.body?.caseId) {
      setCaseId(response.body.caseId);
      setFullName(body.fullName);
    }
    formSubmissionLock = false;
  };

  const resetForm = () => {
    reset();
    setCaseId('');
  };

  const copyToClipboard = (text: string) => {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'absolute';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    return new Promise<void>((resolve, reject) => {
      if (document.execCommand('copy')) {
        resolve();
      } else {
        reject();
      }
      textArea.remove();
    });
  };

  const copyCaseId = () =>
    copyToClipboard(`${t.successModal.ticketLabel} ${caseId}`);

  // Extract first name for the modal greeting
  const firstName = fullName.trim().split(' ')[0] || '';

  return (
    <div>
      <form>
        <div>
          <div>
            <div>
              <label className={style.formLabel}>{t.labels.fullName}</label>
              <div>
                <input
                  type="text"
                  style={inputStyle}
                  {...register('fullName', {
                    required: t.errors.nameRequired,
                    pattern: {
                      value: /^[a-zA-Z\s]{0,50}$/,
                      message: t.errors.namePattern,
                    },
                  })}
                />
                {errors.fullName?.message && (
                  <div className="error-msg text-end">
                    {errors.fullName.message}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label>{t.labels.phone}</label>
              <div>
                <input
                  type="tel"
                  id="phone"
                  style={inputStyle}
                  {...register('phone', {
                    required: t.errors.phoneRequired,
                    pattern: {
                      value: /^(?![01])[0-9]{10}$/,
                      message: t.errors.phonePattern,
                    },
                  })}
                />
                {errors.phone?.message && (
                  <div className="error-msg text-end">
                    {errors.phone.message}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <label>{t.labels.email}</label>
            <div>
              <input
                type="email"
                id="email"
                style={inputStyle}
                {...register('email', {
                  required: false,
                  pattern: {
                    value: /^$|^\S+@\S+$/i,
                    message: t.errors.emailPattern,
                  },
                })}
              />
            </div>
          </div>

          <div>
            <label>{t.labels.message}</label>
            <div>
              <textarea
                id="message"
                rows={5}
                style={inputStyle}
                {...register('message', {
                  required: t.errors.messageRequired,
                })}
              />
            </div>
            {errors.message?.message && (
              <div className="error-msg text-end">{errors.message.message}</div>
            )}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            margin: '10px 0',
          }}
        >
          <Button
            variant="contained"
            style={{
              backgroundColor: '#0aa961',
              marginBottom: 12,
              color: 'white',
            }}
            onClick={() => clickSubmit()}
          >
            {t.labels.submitButton}
          </Button>
        </div>
        <br />
      </form>

      <div id="success-modal" style={{ display: caseId ? 'flex' : 'none' }}>
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'lightgrey',
            opacity: '0.5',
            zIndex: 2000,
          }}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            position: 'fixed',
            zIndex: 3000,
          }}
        >
          <div
            style={{
              width: '80%',
              minHeight: '10em',
              backgroundColor: 'white',
              borderRadius: '10px',
              padding: '20px',
              opacity: '1',
            }}
          >
            <h3 style={{ textTransform: 'none' }}>{t.successModal.header}</h3>
            <br />
            <div>{t.successModal.body(firstName)}</div>
            <br />
            <div>
              <span>
                <b>
                  {t.successModal.ticketLabel} {caseId}
                </b>
              </span>{' '}
              &nbsp;
              <span onClick={() => copyCaseId()} style={{ cursor: 'pointer' }}>
                <ContentCopyOutlinedIcon fontSize={'small'} />
              </span>
            </div>
            <br />
            <div style={{ whiteSpace: 'pre-line' }}>
              {t.successModal.queueText}
            </div>
            <br />
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  cursor: 'pointer',
                  border: '2px solid lightgrey',
                  borderRadius: '5px',
                  padding: '8px 16px',
                }}
                onClick={() => {
                  resetForm();
                }}
              >
                {t.successModal.closeButton}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientHubContactWebform;
